import config from "./config";
import authStore from "./authStore";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { DATA_RESPONSE_CODE } from "./shared/dataResponseCode";

export const headers = (clientHeaders) => {
  const _headers = {
    "Content-Type": "application/json; charset=utf-8",
  };
  if (authStore.authToken && authStore.authToken !== "null") {
    _headers["x-auth-token"] = authStore.authToken;
  }
  return { ..._headers, ...clientHeaders };
};

// factory for generating services
export default function service(pattern, serviceOptions = {}) {
  const parseData = (response) => {
    if (!response.ok) {
      if (response.status === 404) {
        return Promise.reject({
          title: 404,
          message: `Data not found at ${response.url}`,
        });
      }
      return Promise.reject({ message: response.statusText });
    }
    return response.json().then((data) => {
      if (!data)
        throw {
          title: "unexpected error",
          message: "The server sent an empty response.",
          data,
        };
      if (data?.responseCode === DATA_RESPONSE_CODE.SECURITY_TOKEN_INVALID) {
        // drop token
        authStore.deauth(DATA_RESPONSE_CODE.SECURITY_TOKEN_INVALID);
      }
      if (data.responseError) {
        return Promise.reject({
          ...data.responseError,
          data,
        });
      } else if (data.responseCode === 0) {
        if (authStore.loggedOut) return;
        authStore.authToken =
          response.headers.get("x-auth-token") ||
          data.model?.xAuthToken ||
          authStore.authToken;
        return Promise.resolve(data.model);
      } else if (data.error && Object.keys(data.error).length) {
        console.error(
          "Request failed.",
          JSON.stringify({
            error: data.error,
            pattern,
            serviceOptions,
            data,
          }),
        );
        return Promise.reject(data.error);
      } else {
        return Promise.reject({
          icon: "unknown",
          error: "An unexpected error occurred.",
          data,
        });
      }
    });
  };

  const catchError = (err, response) => {
    let errorMessage =
      err.description || err.details || err.title || response?.statusText || "";
    if (!errorMessage && err.message) {
      errorMessage = err.message;
      if (err.message === "Failed to fetch") {
        // This is the message provided when the network is disconnected
        err.title = "Request Failed";
        err.message =
          "Please check your network connection or contact your system administrator.";
      }
    }
    if (err.details) {
      errorMessage += " " + err.details;
    }

    const errorModel = translateErrorMessage({
      title: err.title || "error",
      text: err.message || errorMessage,
    });

    if (err.ABORT_ERR || err.name === "AbortError") {
      console.warn(
        "fetch aborted",
        JSON.stringify({
          err,
          pattern,
          serviceOptions,
        }),
      );
    } else if (serviceOptions?.quiet) {
      console.error(
        "fetch error",
        JSON.stringify({
          err,
          pattern,
          serviceOptions,
        }),
      );
    } else {
      if (!swal.isVisible())
        swal.fire({
          ...errorModel,
          icon: "error",
          allowOutsideClick: false,
        });
    }

    return Promise.reject(err);
  };
  const signal = serviceOptions?.controller?.signal;

  const get = async (options = {}) => {
    let response = await fetch(getUri(pattern, options), {
      headers: headers(),
      credentials: "same-origin",
      signal,
      cache: serviceOptions.cache || "no-cache",
    }).catch((err) => catchError(err, response, options));
    return parseData(response).catch((err) =>
      catchError(err, response, options),
    );
  };

  const post = async ({ query = {}, model = {}, clientHeaders = {} }) => {
    let response;
    try {
      response = await fetch(getUri(pattern, query), {
        method: "POST",
        body: JSON.stringify(model),
        headers: headers(clientHeaders),
        signal,
      });
      return parseData(response).catch((err) => catchError(err, response));
    } catch (err) {
      catchError(err, response);
    }
  };

  return { get, post };
}

function getUri(pattern, query = {}) {
  let _query = { ...query };
  let path = pattern.replace(/:(\w+)/g, function (match) {
    let paramVal = query[match[1]];
    delete _query[match[1]];
    return paramVal || "";
  });
  return (
    config.apiRoot +
    path +
    serialize({
      siteid: config.siteid,
      ..._query,
    })
  );
}

export function serialize(obj = {}) {
  let _keys = Object.keys(obj);
  return _keys.length
    ? "?" +
        _keys
          .reduce((a, k) => {
            if (typeof obj[k] !== "undefined") {
              if (Array.isArray(obj[k])) {
                a.push(k + "=" + obj[k].map(encodeURIComponent).join(","));
              } else {
                a.push(k + "=" + encodeURIComponent(obj[k]));
              }
            }
            return a;
          }, [])
          .join("&")
    : "";
}

const errorTranslations = [
  {
    match: {
      text: "Error: the Description is a required field.",
    },
    value: {
      title: "Description Required",
      text: "The Description is a required field",
    },
  },
];
function translateErrorMessage(errorModel) {
  const translation = errorTranslations.find((translation) =>
    [...Object.entries(translation.match)].every(([key, val]) => {
      return transformText(val) === transformText(errorModel[key]);
    }),
  );
  return { ...errorModel, ...translation?.value };
}
function transformText(txt) {
  return txt
    .trim()
    .replaceAll(/\s+/g, " ") // strip redundant whitespace characters and uses normal space.
    .replaceAll(/[^\w\s]/g, "") // strip out punctuation
    .toLowerCase();
}
